import { useEffect, useCallback } from 'react';

/**
 * Generate a field ID with optional suffix based on item key
 */
export const getFieldId = (fieldName: string, key: string): string => {
  const suffix = key === 'global' ? '' : ` (${key})`;
  return `${fieldName}${suffix}`;
};

/**
 * Generate HTML element ID for anchors
 */
export const getHTMLId = (validationId: string, key: string): string => {
  if (key === 'global') return validationId;
  return `${validationId}-${key}`;
};

/**
 * Clear hash from URL without triggering navigation
 */
export const clearUrlHash = (): void => {
  if (window.location.hash) {
    window.history.pushState(
      '',
      document.title,
      window.location.pathname + window.location.search,
    );
  }
};

/**
 * Custom hook for handling modal open/close with hash-based navigation
 */
export const useResolverModal = (
  isCompact: boolean,
  fieldIds: string[],
  htmlId: string,
  onOpen: () => void,
  onClose: () => void,
) => {
  // Auto-open modal when hash matches our field IDs
  useEffect(() => {
    if (!isCompact) return;

    const handleHashChange = () => {
      const hash = decodeURIComponent(window.location.hash.slice(1));
      if (fieldIds.includes(hash) || hash === htmlId) {
        onOpen();
      }
    };

    // Check on mount
    handleHashChange();

    // Listen for hash changes
    window.addEventListener('hashchange', handleHashChange);
    return () => window.removeEventListener('hashchange', handleHashChange);
  }, [isCompact, fieldIds, htmlId, onOpen]);

  // Close modal and clear hash from URL
  const handleModalClose = useCallback(() => {
    clearUrlHash();
    onClose();
  }, [onClose]);

  return { handleModalClose };
};

/**
 * Parse date string in YYYY-MM-DD format to Date object
 */
export const parseDateString = (
  dateStr: string | null | undefined,
): Date | null => {
  if (!dateStr) return null;
  const match = dateStr.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!match) return null;
  return new Date(
    parseInt(match[1]),
    parseInt(match[2]) - 1,
    parseInt(match[3]),
  );
};

/**
 * Format Date object to YYYY-MM-DD string
 */
export const formatDateToString = (date: Date | null): string | null => {
  if (!date) return null;
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
};

const BINDING_AUTHORITY_DAYS: Record<string, number> = {
  wawanesa: 60,
  economical: 90,
  definity: 90,
  commonwell: 120,
};

const DEFAULT_BINDING_AUTHORITY_DAYS = 30;

export const BINDING_AUTHORITY_MESSAGE =
  'The noted approval does not fall within the binding authority timeline for this market.';

export const MARKET_MISMATCH_MESSAGE =
  'The market approval must come from the market being quoted.';

const marketFamily = (market: string | null | undefined): string => {
  const words = (market || '')
    .toLowerCase()
    .replace(/[^a-z]+/g, ' ')
    .split(' ')
    .filter((word) => word && word !== 'the');

  return words[0] || '';
};

export const isApprovalMarketMatching = (
  approvalMarket: string | null | undefined,
  quotedMarket: string | null | undefined,
): boolean => {
  if (!approvalMarket || !quotedMarket) return true;

  const approval = marketFamily(approvalMarket);
  const quoted = marketFamily(quotedMarket);
  if (!approval || !quoted) return true;

  return approval === quoted;
};

export const isEmailOnlyMarket = (
  quotedMarket: string | null | undefined,
): boolean => marketFamily(quotedMarket) === 'unica';

export const getBindingAuthorityDays = (
  quotedMarket: string | null | undefined,
): number =>
  BINDING_AUTHORITY_DAYS[marketFamily(quotedMarket)] ??
  DEFAULT_BINDING_AUTHORITY_DAYS;

export const buildMarketApprovalContext = (
  quotedMarket: string | null | undefined,
  effectiveDate: string | null | undefined,
): string => {
  if (!quotedMarket) return '';

  return [
    `The market being quoted is ${quotedMarket}.`,
    effectiveDate &&
      `The policy effective date is ${effectiveDate.slice(0, 10)}.`,
    `The market approval must come from ${quotedMarket}; an approval from any other market is a fail.`,
    `${quotedMarket} allows market approval up to ${getBindingAuthorityDays(quotedMarket)} days before the policy effective date; an older approval is a fail.`,
    isEmailOnlyMarket(quotedMarket) &&
      `${quotedMarket} accepts market approval by email only. Fail the remark only if it says the approval was obtained by phone or by a call; a date and time on an email approval is normal and is not a phone call.`,
  ]
    .filter(Boolean)
    .join(' ');
};

export const isApprovalWithinBindingAuthority = (
  approvalDate: string | null | undefined,
  effectiveDate: string | null | undefined,
  quotedMarket: string | null | undefined,
): boolean => {
  const approval = parseDateString(approvalDate);
  const effective = parseDateString(effectiveDate?.slice(0, 10));
  if (!approval || !effective) return true;

  const allowedDays = getBindingAuthorityDays(quotedMarket);

  const days = Math.round(
    (effective.getTime() - approval.getTime()) / (24 * 60 * 60 * 1000),
  );

  return days <= allowedDays;
};
